import { format, isToday } from "date-fns";
import { DayCapacity } from "./plannerUtils";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface DayColumnProps {
  capacity: DayCapacity;
  isCompact?: boolean;
}

export function DayColumn({ capacity, isCompact = false }: DayColumnProps) {
  const isCurrentDay = isToday(capacity.date);

  // Determine color based on utilization
  const getUtilizationColor = () => {
    if (!capacity.isWorkingDay) return "bg-muted/50";
    if (capacity.utilizationPercentage <= 80) return "bg-green-500/20";
    if (capacity.utilizationPercentage <= 100) return "bg-yellow-500/20";
    return "bg-red-500/20";
  };

  const getProgressColor = () => {
    if (!capacity.isWorkingDay) return "bg-muted-foreground/20";
    if (capacity.utilizationPercentage <= 80) return "bg-green-500";
    if (capacity.utilizationPercentage <= 100) return "bg-yellow-500";
    return "bg-red-500";
  };

  const progressWidth = Math.min(100, capacity.utilizationPercentage);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex flex-col items-center border-r border-border last:border-r-0 transition-colors",
              isCompact ? "min-w-[40px] px-1 py-1" : "min-w-[60px] px-2 py-2",
              !capacity.isWorkingDay && "bg-muted/30",
              isCurrentDay && "bg-primary/5 ring-1 ring-primary/20"
            )}
          >
            {/* Day name */}
            <span
              className={cn(
                "font-medium",
                isCompact ? "text-[10px]" : "text-xs",
                isCurrentDay ? "text-primary" : "text-muted-foreground"
              )}
            >
              {format(capacity.date, isCompact ? "EEE" : "EEE")}
            </span>

            {/* Day number */}
            <span
              className={cn(
                "font-bold",
                isCompact ? "text-sm" : "text-base",
                isCurrentDay ? "text-primary" : "text-foreground"
              )}
            >
              {format(capacity.date, "d")}
            </span>

            {/* Capacity bar */}
            {capacity.isWorkingDay && (
              <div className={cn("w-full mt-1", isCompact ? "h-1" : "h-1.5")}>
                <div className={cn("w-full h-full rounded-full", getUtilizationColor())}>
                  <div
                    className={cn("h-full rounded-full transition-all", getProgressColor())}
                    style={{ width: `${progressWidth}%` }}
                  />
                </div>
              </div>
            )}

            {/* Hours label */}
            {!isCompact && capacity.isWorkingDay && (
              <span className="text-[10px] text-muted-foreground mt-0.5">
                {capacity.usedHours.toFixed(0)}/{capacity.totalCapacity}h
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <div className="text-sm">
            <p className="font-medium">{format(capacity.date, "EEEE, MMM d")}</p>
            {capacity.isWorkingDay ? (
              <>
                <p className="text-muted-foreground">
                  {capacity.usedHours.toFixed(1)}h / {capacity.totalCapacity}h
                </p>
                <p className="text-muted-foreground">
                  {capacity.utilizationPercentage.toFixed(0)}% utilized
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">Non-working day</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
